import { Divider } from '@eventbrite/eds-divider';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import {
    CurrentLocationIcon,
    MagnifyingGlassIcon,
    MapPinIcon,
    Typography,
} from '@eventbrite/marmalade';
import { logEvent } from '@eventbrite/statsig';
import loadable from '@loadable/component';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { STATSIG_SEARCH_BAR_CLICK } from '../../../../constants';
import { useGlobalNavContext } from '../../../../state';
import { useGetSearchBarVariant } from '../../../../utils';
import {
    getHeapDataAttribute,
    HeapAttributes,
} from '../../../../utils/heap-data';
import './SearchBar.module.scss';
import styles from './SearchBar.module.scss';

const LazySearchTakeover = loadable.lib(
    () => import('@eventbrite/search-takeover'),
);

enum IconColor {
    default = 'default',
    blue = 'blue',
}

/**TODO: Search Page Hierarchy experiment cleanup EB-228109: if this experiment is successful,
 * we should elevate SearchTakeoverRenderer to a single location to avoid multiple re-renders
 *  **/
//TODO: start Hierarchy experiment cleanup EB-228109.
const ResultsPageSearchBar = () => {
    const { Teams, Features, Components, Actions } = HeapAttributes;

    const { searchBar } = useGlobalNavContext();

    const [showSearch, setShowSearch] = useState(false);
    const [locationIconColor, setLocationIconColor] = useState<string>(
        IconColor.default,
    );

    useEffect(() => {
        if (searchBar?.isUsingCurrentLocation) {
            setLocationIconColor(IconColor.blue);
        } else {
            setLocationIconColor(IconColor.default);
        }
    }, [searchBar?.isUsingCurrentLocation]);

    const handleClick = () => {
        logEvent(STATSIG_SEARCH_BAR_CLICK);
        setShowSearch(true);
    };
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            logEvent(STATSIG_SEARCH_BAR_CLICK);
            setShowSearch(true);
        }
    };
    function handleClose() {
        setShowSearch(false);
    }
    const isSearchBarExperiment = useGetSearchBarVariant();

    const isMobile = true;

    const contentClass = classNames(styles.content, styles.searchPage);
    const buttonClass = classNames(styles.main, styles.override);

    const formatLocationString = (
        location?: string | GenericLazyString,
        isMobile = false,
    ): string | GenericLazyString | null => {
        if (location === undefined) {
            return null;
        }
        if (location.toLowerCase().includes('online')) {
            return 'Online';
        }
        return isMobile ? `in ${location}` : location;
    };

    const mobileLocation = formatLocationString(searchBar?.location, isMobile);
    const locationDesktop = formatLocationString(searchBar?.location);
    return (
        <>
            {searchBar?.renderSearchBar && isSearchBarExperiment ? (
                <button
                    className={buttonClass}
                    data-testid="header-search"
                    onClick={handleClick}
                    onKeyDown={handleKeyDown}
                    aria-label={gettext('open search bar').toString()}
                    data-heap-id={getHeapDataAttribute({
                        team: Teams.SEO,
                        feature: Features.GlobalNav,
                        component: Components.SearchBar,
                        name: 'search-bar',
                        action: Actions.Click,
                    })}
                >
                    <>
                        <Typography
                            variant="body-md"
                            as="span"
                            color="neutral-700"
                            className={contentClass}
                        >
                            <span
                                className={styles.searchLabelWrapper}
                                style={{ gridColumn: 1 }}
                            >
                                <MagnifyingGlassIcon size="small" />
                                <span className={styles.desktopLabel}>
                                    {searchBar?.desktopLabel}
                                </span>
                                <span className={styles.mobileLabel}>
                                    {searchBar?.mobileLabel}
                                </span>
                                <span className={styles.mobileLocation}>
                                    {mobileLocation}
                                </span>
                            </span>
                            <span
                                className={styles.searchPageComponentWrapper}
                                style={{ gridColumn: 2 }}
                            >
                                <Divider direction="vertical" color="ui-500" />
                            </span>
                            <span
                                className={styles.searchPageComponentWrapper}
                                style={{ gridColumn: 3 }}
                            >
                                <div className={styles.locationWrapper}>
                                    <span>
                                        <span className={styles.locationLabel}>
                                            <MapPinIcon size="small" />
                                            {locationDesktop}
                                        </span>
                                    </span>
                                    <span>
                                        <CurrentLocationIcon
                                            size="small"
                                            style={{ gridColumn: 4 }}
                                            color={locationIconColor}
                                        />
                                    </span>
                                </div>
                            </span>
                        </Typography>

                        {showSearch ? (
                            <LazySearchTakeover data-testid="search-takeover">
                                {({
                                    SearchTakeover,
                                }: {
                                    SearchTakeover: any;
                                }) => (
                                    <SearchTakeover
                                        domNodeId="root"
                                        onClose={handleClose}
                                        id="search-takeover"
                                        isVisible={showSearch}
                                    />
                                )}
                            </LazySearchTakeover>
                        ) : null}
                    </>
                </button>
            ) : undefined}
        </>
    );
};

export default ResultsPageSearchBar;
